import { defineStore } from 'pinia';
import { RouteRecordRaw } from 'vue-router';
import { asyncRoutes, constantRoutes, catchAllRoutes } from '@/router';
import { intersection } from '@/utils/index';
import { markFourthLevelMenu } from '@/utils/menu';
import type { MenuItem } from '@/types/menu';
import { useTenantStore } from '@/store/tenant';

type State = {
  permissionMenus: TypeObject[],
  permissionRoutes: RouteRecordRaw[],
  accessedRoutes: RouteRecordRaw[]
}

/**
 * 获取用户有权限的路由
 * @param routes
 * @param menus
 * @param fmPermission FM权限编码前缀
 */
export function filterAsyncRoutes(
  routes: RouteRecordRaw[],
  authCodes: Array<any>,
  fmPermission: string
): RouteRecordRaw[] {
  const result: Array<RouteRecordRaw> = [];

  routes.forEach((route: RouteRecordRaw) => {
    const routeAuthCodes: string[] | any = route?.meta?.authCodes || [];

    const matchedCodes = intersection(
      authCodes,
      routeAuthCodes.map((item: string) => `${fmPermission}${item}`)
    );
    if (matchedCodes.length > 0) {
      if (route.children) {
        route.children = filterAsyncRoutes(route.children, authCodes, fmPermission);
      }
      result.push(route);
    }
  });

  return result;
}

/**
 * @description: 更新路由的redirect指向
 * @param {RouteRecordRaw} routes
 */
export function updateRedirect(routes: RouteRecordRaw[]) {
  routes.forEach((route) => {
    if (route.children && route.children.length > 0) {
      route.redirect = route.children[0].path;
      updateRedirect(route.children);
    }
  });
}

const formatMenus = (menus: TypeObject[], authCodes: Array<string>, fmPermission: string, pPath = '') => {
  menus.forEach((menu) => {
    const matchedCodes = intersection(
      authCodes,
      menu.authCodes.map((item: string) => `${fmPermission}${item}`)
    );
    menu.disabled = matchedCodes.length <= 0;
    if (pPath) {
      menu.parentPath = pPath;
    }

    if (menu.children && menu.children.length > 0) {
      formatMenus(menu.children, authCodes, fmPermission, menu.path);
    }
  });
};

export const usePermissionStore = defineStore('_permission', {
  state: (): State => ({
    permissionMenus: [], // 菜单列表
    permissionRoutes: [], // 最终要展示到菜单的路由列表
    accessedRoutes: [], // 匹配权限的菜单列表
  }),
  actions: {
    generateRoutes(authCodes: Array<string>, menuList: MenuItem[]): Promise<TypeObject> {
      this.permissionMenus = [...menuList];

      // 标记第4级菜单为tab菜单
      markFourthLevelMenu(this.permissionMenus);

      return new Promise((resolve) => {
        // 从租户 store 获取权限编码前缀
        const tenantStore = useTenantStore();
        const fmPermission = tenantStore.fmPermission;

        const accessedRoutes = filterAsyncRoutes(asyncRoutes, authCodes, fmPermission);
        updateRedirect(accessedRoutes);
        this.accessedRoutes = accessedRoutes;

        this.permissionRoutes = [...constantRoutes, ...accessedRoutes, ...catchAllRoutes];

        /*
         * 根据用户权限code控制菜单的显示与隐藏
         * 先隐藏，先用前端写死控制，直接在munulist里面写死
         * formatMenus(this.permissionMenus, authCodes);
         */
        formatMenus(this.permissionMenus, authCodes, fmPermission);
        resolve({
          accessedRoutes,
          permissionMenus: this.permissionMenus,
          permissionRoutes: this.permissionRoutes,
        });
      });
    },
  },
});
